import { supabase } from '../lib/supabase';

const SESSION_STORAGE_KEY = 'kiosk_session';

export interface KioskSessionStored {
  token: string;
  leagueSlug: string;
  participantId: string;
  displayName: string;
}

export interface KioskLeagueBranding {
  id: string;
  slug: string;
  name: string;
  description: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  logo_url: string;
  hero_image_url: string;
  background_image_url: string;
  signup_headline: string;
  signup_subtext: string;
  sponsor_logos: string[];
  sponsor_links: string[];
  scoring_mode?: 'best' | 'latest' | 'average';
  leaderboard_top_n: number | null;
  signup_mode: 'email_verified' | 'name_only' | 'passcode';
  allow_playback_default: boolean;
  rules: string[] | null;
  rules_markdown: string | null;
}

export interface KioskLeaderboardEntry {
  participant_id: string;
  display_name: string;
  score: number;
  submissions_count: number;
  allow_playback: boolean;
  last_submission_at: string | null;
  rank: number;
}

export interface KioskLeaderboardResponse {
  league: KioskLeagueBranding;
  entries: KioskLeaderboardEntry[];
}

export interface KioskPlayableSubmission {
  submission_id: string;
  participant_id: string;
  display_name: string;
  recording_url: string;
  recording_type: string;
  score: number;
  song_title: string | null;
  song_artist: string | null;
  backing_track_url: string | null;
  backing_track_lufs: number | null;
  vocals_lufs: number | null;
  mixed_audio_url: string | null;
  mix_status: string | null;
  mixed_video_url: string | null;
}

export interface KioskSongEntry {
  id: string;
  title: string;
  artist: string;
  cover_url: string | null;
  audio_url: string | null;
  full_mix_url: string | null;
  lyrics_url: string | null;
  display_order: number;
}

export function resolveLyricsPublicUrl(lyricsUrl: string | null | undefined): string | null {
  if (!lyricsUrl) return null;
  if (lyricsUrl.startsWith('http')) return lyricsUrl;
  const base = import.meta.env.VITE_SUPABASE_URL as string;
  const path = lyricsUrl.split('/').pop() || '';
  return `${base}/storage/v1/object/public/song_lyrics/${path}`;
}

export interface KioskSubmissionStatus {
  id: string;
  score: number | null;
  pitch_accuracy: number | null;
  timing_accuracy: number | null;
  submitted_at: string;
  scored_at: string | null;
}

function storeSession(session: KioskSessionStored) {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

export function getStoredKioskSession(): KioskSessionStored | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearKioskSession() {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export const KioskService = {
  async requestVerification(leagueSlug: string, email: string, displayName: string, marketingOptIn: boolean) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    const res = await fetch(`${supabaseUrl}/functions/v1/send-kiosk-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({
        league_slug: leagueSlug,
        email,
        display_name: displayName,
        marketing_opt_in: marketingOptIn,
      }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to send verification');
    return json as { verification_id: string; email_delivered: boolean; debug_code?: string };
  },

  async signupNameOnly(leagueSlug: string, displayName: string) {
    const { data, error } = await supabase.rpc('kiosk_signup_name_only', {
      p_league_slug: leagueSlug,
      p_display_name: displayName,
      p_user_agent: navigator.userAgent,
    });
    if (error) throw new Error(error.message);
    const result = data as {
      session_token: string;
      participant_id: string;
      kiosk_user_id: string;
      league_id: string;
      display_name: string;
      allow_playback: boolean;
    };
    storeSession({
      token: result.session_token,
      leagueSlug,
      participantId: result.participant_id,
      displayName: result.display_name,
    });
    return result;
  },

  async signupWithPasscode(leagueSlug: string, displayName: string, passcode: string) {
    const { data, error } = await supabase.rpc('kiosk_signup_passcode', {
      p_league_slug: leagueSlug,
      p_display_name: displayName,
      p_passcode: passcode,
      p_user_agent: navigator.userAgent,
    });
    if (error) throw new Error(error.message);
    const result = data as {
      session_token: string;
      participant_id: string;
      kiosk_user_id: string;
      league_id: string;
      display_name: string;
      allow_playback: boolean;
    };
    storeSession({
      token: result.session_token,
      leagueSlug,
      participantId: result.participant_id,
      displayName: result.display_name,
    });
    return result;
  },

  async submitRecording(params: {
    blob: Blob;
    songId: string;
    leagueSlug: string;
    durationSeconds: number;
    recordingType?: 'audio' | 'video';
    testmode?: boolean;
  }): Promise<{ submission_id: string; recording_id?: string; recording_url: string; scoring_started: boolean }> {
    const session = getStoredKioskSession();
    if (!session) throw new Error('Not signed in to kiosk');
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    const recordingType = params.recordingType || 'audio';

    const minBlobSize = 1024;
    if (!params.blob || params.blob.size < minBlobSize) {
      throw new Error(
        'Recording failed — no audio was captured. Please check your microphone connection and try again.'
      );
    }

    const signRes = await fetch(`${supabaseUrl}/functions/v1/kiosk-create-upload-url`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_token: session.token,
        recording_type: recordingType,
        mime_type: params.blob.type || (recordingType === 'video' ? 'video/webm' : 'audio/webm'),
      }),
    });
    const signJson = await signRes.json();
    if (!signRes.ok) throw new Error(signJson.error || 'Failed to create upload URL');

    const { bucket, path, token, public_url } = signJson as {
      bucket: string;
      path: string;
      token: string;
      public_url: string;
    };

    const { error: uploadErr } = await supabase.storage
      .from(bucket)
      .uploadToSignedUrl(path, token, params.blob, {
        contentType: params.blob.type || undefined,
        upsert: false,
      });
    if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`);

    // Verify the file actually landed in storage
    const verifyRes = await fetch(public_url, { method: 'HEAD' });
    if (!verifyRes.ok) {
      throw new Error('Upload could not be verified — the file did not save correctly. Please try recording again.');
    }
    const contentLength = parseInt(verifyRes.headers.get('content-length') || '0', 10);
    if (contentLength < minBlobSize) {
      throw new Error('Upload verification failed — the saved file appears empty. Please try recording again.');
    }

    const res = await fetch(`${supabaseUrl}/functions/v1/kiosk-submit-recording`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        session_token: session.token,
        song_id: params.songId,
        league_slug: params.leagueSlug,
        duration_seconds: params.durationSeconds,
        recording_type: recordingType,
        recording_path: path,
        recording_url: public_url,
        ...(params.testmode && { testmode: true }),
      }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to finalize recording');
    return json;
  },

  async getSubmissionAnalysis(submissionId: string): Promise<Record<string, any> | null> {
    const session = getStoredKioskSession();
    if (!session) throw new Error('Not signed in to kiosk');
    const { data, error } = await supabase.rpc('kiosk_get_submission_analysis', {
      p_session_token: session.token,
      p_submission_id: submissionId,
    });
    if (error) throw new Error(error.message);
    return (data as Record<string, any>) ?? null;
  },

  async getSubmissionStatus(submissionId: string): Promise<KioskSubmissionStatus | null> {
    const { data, error } = await supabase.rpc('kiosk_get_submission_status', {
      p_submission_id: submissionId,
    });
    if (error) throw new Error(error.message);
    return (data as KioskSubmissionStatus) ?? null;
  },

  async verify(leagueSlug: string, email: string, code: string) {
    const { data, error } = await supabase.rpc('kiosk_signup_verify', {
      p_league_slug: leagueSlug,
      p_email: email,
      p_code: code,
      p_user_agent: navigator.userAgent,
    });
    if (error) throw new Error(error.message);
    const result = data as {
      session_token: string;
      participant_id: string;
      kiosk_user_id: string;
      league_id: string;
      display_name: string;
      allow_playback: boolean;
    };
    storeSession({
      token: result.session_token,
      leagueSlug,
      participantId: result.participant_id,
      displayName: result.display_name,
    });
    return result;
  },

  async joinQueue() {
    const session = getStoredKioskSession();
    if (!session) throw new Error('Not signed in to kiosk');
    const { data, error } = await supabase.rpc('kiosk_join_queue', {
      p_session_token: session.token,
    });
    if (error) throw new Error(error.message);
    return data;
  },

  async submitScore(params: {
    recordingId: string;
    songId: string;
    score: number;
    pitchAccuracy?: number;
    timingAccuracy?: number;
  }) {
    const session = getStoredKioskSession();
    if (!session) throw new Error('Not signed in to kiosk');
    const { data, error } = await supabase.rpc('kiosk_submit_score', {
      p_session_token: session.token,
      p_recording_id: params.recordingId,
      p_song_id: params.songId,
      p_score: params.score,
      p_pitch_accuracy: params.pitchAccuracy ?? null,
      p_timing_accuracy: params.timingAccuracy ?? null,
    });
    if (error) throw new Error(error.message);
    return data;
  },

  async updatePlaybackPreference(allowPlayback: boolean) {
    const session = getStoredKioskSession();
    if (!session) throw new Error('Not signed in to kiosk');
    const { data, error } = await supabase.rpc('kiosk_update_playback_preference', {
      p_session_token: session.token,
      p_allow_playback: allowPlayback,
    });
    if (error) throw new Error(error.message);
    return data;
  },

  async getMe() {
    const session = getStoredKioskSession();
    if (!session) return null;
    const { data, error } = await supabase.rpc('kiosk_get_me', {
      p_session_token: session.token,
    });
    if (error) {
      if (error.message.toLowerCase().includes('session')) {
        clearKioskSession();
      }
      throw new Error(error.message);
    }
    return data;
  },

  async getLeaderboard(leagueSlug: string, limit?: number): Promise<KioskLeaderboardResponse> {
    const { data, error } = await supabase.rpc('kiosk_leaderboard_public', {
      p_league_slug: leagueSlug,
      p_limit: limit ?? null,
    });
    if (error) throw new Error(error.message);
    return data as KioskLeaderboardResponse;
  },

  async getSharedSubmission(submissionId: string) {
    const { data, error } = await supabase.rpc('kiosk_get_shared_submission', {
      p_submission_id: submissionId,
    });
    if (error) throw new Error(error.message);
    return data;
  },

  async getLeagueSongs(leagueSlug: string): Promise<KioskSongEntry[]> {
    const { data, error } = await supabase.rpc('kiosk_league_songs_public', {
      p_league_slug: leagueSlug,
    });
    if (error) throw new Error(error.message);
    return (data as KioskSongEntry[]) || [];
  },

  async deleteSelf() {
    const session = getStoredKioskSession();
    if (!session) return;
    const { error } = await supabase.rpc('kiosk_users_delete_self', {
      p_session_token: session.token,
    });
    if (error) throw new Error(error.message);
    clearKioskSession();
  },

  async getPlayableSubmissions(leagueSlug: string): Promise<KioskPlayableSubmission[]> {
    const { data, error } = await supabase.rpc('kiosk_get_playable_submissions', {
      p_league_slug: leagueSlug,
    });
    if (error) throw new Error(error.message);
    return (data as KioskPlayableSubmission[]) || [];
  },

  async getLikes(leagueSlug: string): Promise<{ counts: Record<string, number>; my_likes: string[] }> {
    const session = getStoredKioskSession();
    const { data, error } = await supabase.rpc('kiosk_get_likes', {
      p_league_slug: leagueSlug,
      p_session_token: session?.token ?? null,
    });
    if (error) throw new Error(error.message);
    return data as { counts: Record<string, number>; my_likes: string[] };
  },

  async toggleLike(targetParticipantId: string): Promise<{ liked: boolean; like_count: number }> {
    const session = getStoredKioskSession();
    if (!session) throw new Error('Not signed in to kiosk');
    const { data, error } = await supabase.rpc('kiosk_toggle_like', {
      p_session_token: session.token,
      p_target_participant_id: targetParticipantId,
    });
    if (error) throw new Error(error.message);
    return data as { liked: boolean; like_count: number };
  },

  // Admin operations
  async adminListLeagues() {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  async adminGetLeague(id: string) {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },

  async adminCreateLeague(payload: Partial<KioskLeagueBranding> & { slug: string; name: string; status?: string }) {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .insert(payload)
      .select()
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },

  async adminUpdateLeague(id: string, patch: Record<string, unknown>) {
    const { data, error } = await supabase
      .from('kiosk_leagues')
      .update({ ...patch, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data;
  },

  async adminDeleteLeague(id: string) {
    const { error } = await supabase.from('kiosk_leagues').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  async adminListLeagueSongs(leagueId: string) {
    const { data, error } = await supabase
      .from('kiosk_league_songs')
      .select('id, song_id, display_order, songs:song_id(id, title, artist, cover_url)')
      .eq('league_id', leagueId)
      .order('display_order');
    if (error) throw new Error(error.message);
    return data || [];
  },

  async adminAddLeagueSong(leagueId: string, songId: string, displayOrder: number) {
    const { error } = await supabase
      .from('kiosk_league_songs')
      .insert({ league_id: leagueId, song_id: songId, display_order: displayOrder });
    if (error) throw new Error(error.message);
  },

  async adminRemoveLeagueSong(id: string) {
    const { error } = await supabase.from('kiosk_league_songs').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  async adminListParticipants(leagueId: string) {
    const { data, error } = await supabase
      .from('kiosk_participants')
      .select('*, kiosk_user:kiosk_user_id(id, email, display_name, marketing_opt_in, email_verified_at, created_at)')
      .eq('league_id', leagueId)
      .order('joined_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  async adminListSubmissions(leagueId: string) {
    const { data, error } = await supabase
      .from('kiosk_submissions')
      .select('*, participant:participant_id(display_name, kiosk_user_id), song:song_id(title, artist, audio_url, backing_track_lufs), kiosk_recording:recording_id(id, recording_type, vocals_only_url, vocals_lufs, vocals_preprocessed_url, mixed_audio_url, mix_status, mixed_video_url, video_mux_status), performances!kiosk_submission_id(performance_analysis(time_offset_data))')
      .eq('league_id', leagueId)
      .order('submitted_at', { ascending: false });
    if (error) throw new Error(error.message);
    return data || [];
  },

  async adminDeleteSubmission(submissionId: string) {
    const { data: sub, error: fetchError } = await supabase
      .from('kiosk_submissions')
      .select('id, recording_id, recording_url')
      .eq('id', submissionId)
      .maybeSingle();
    if (fetchError) throw new Error(fetchError.message);
    if (!sub) throw new Error('Submission not found');

    const { error: delError } = await supabase
      .from('kiosk_submissions')
      .delete()
      .eq('id', submissionId);
    if (delError) throw new Error(delError.message);

    const url: string | null = (sub as { recording_url?: string | null }).recording_url ?? null;
    if (url) {
      const marker = '/kiosk_recordings/';
      const idx = url.indexOf(marker);
      if (idx >= 0) {
        const path = url.substring(idx + marker.length).split('?')[0];
        if (path) {
          try { await supabase.storage.from('kiosk_recordings').remove([path]); } catch { /* ignore */ }
        }
      }
    }

    const recId = (sub as { recording_id?: string | null }).recording_id;
    if (recId) {
      try { await supabase.from('kiosk_recordings').delete().eq('id', recId); } catch { /* ignore */ }
    }
  },

  async adminDeleteParticipantSubmissions(participantId: string) {
    const { data: subs, error: fetchError } = await supabase
      .from('kiosk_submissions')
      .select('id')
      .eq('participant_id', participantId);
    if (fetchError) throw new Error(fetchError.message);
    if (!subs || subs.length === 0) return 0;
    for (const s of subs) {
      await this.adminDeleteSubmission(s.id);
    }
    await supabase
      .from('kiosk_participants')
      .update({ status: 'registered' })
      .eq('id', participantId);
    return subs.length;
  },

  async adminDeleteParticipant(participantId: string) {
    await this.adminDeleteParticipantSubmissions(participantId);
    const { error } = await supabase
      .from('kiosk_participants')
      .delete()
      .eq('id', participantId);
    if (error) throw new Error(error.message);
  },

  async adminUpdateParticipantStatus(id: string, status: string) {
    const { error } = await supabase
      .from('kiosk_participants')
      .update({ status })
      .eq('id', id);
    if (error) throw new Error(error.message);
  },

  async adminListLeagueAdmins(leagueId: string) {
    const { data: admins, error } = await supabase
      .from('kiosk_league_admins')
      .select('id, user_id, role, created_at')
      .eq('league_id', leagueId);
    if (error) throw new Error(error.message);
    if (!admins || admins.length === 0) return [];
    const ids = admins.map((a) => a.user_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username, full_name, avatar_url')
      .in('id', ids);
    const byId = new Map((profiles || []).map((p) => [p.id as string, p]));
    return admins.map((a) => ({ ...a, profile: byId.get(a.user_id) || null }));
  },

  async adminSearchUsers(query: string) {
    if (!query || query.length < 2) return [];
    const { data, error } = await supabase.rpc('search_users', { query });
    if (error) throw new Error(error.message);
    return (data || []) as Array<{ id: string; username: string; full_name: string; email: string }>;
  },

  async adminAddLeagueAdmin(leagueId: string, userId: string, role: 'owner' | 'manager' = 'manager') {
    const { error } = await supabase
      .from('kiosk_league_admins')
      .insert({ league_id: leagueId, user_id: userId, role });
    if (error) throw new Error(error.message);
  },

  async adminUploadAsset(leagueId: string, file: File, kind: string): Promise<string> {
    const ext = (file.name.split('.').pop() || 'png').toLowerCase();
    const path = `${leagueId}/${kind}-${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('kiosk_assets')
      .upload(path, file, { contentType: file.type || 'image/png', upsert: true });
    if (error) throw new Error(error.message);
    const { data } = supabase.storage.from('kiosk_assets').getPublicUrl(path);
    return data.publicUrl;
  },

  async adminRemoveLeagueAdmin(id: string) {
    const { error } = await supabase.from('kiosk_league_admins').delete().eq('id', id);
    if (error) throw new Error(error.message);
  },

  async adminRescoreSubmission(submissionId: string) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    const res = await fetch(`${supabaseUrl}/functions/v1/kiosk-rescore-v2`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ submission_id: submissionId }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Rescore failed');
    return data;
  },

  async adminGetSubmissionAnalysisUpdatedAt(submissionId: string): Promise<string | null> {
    const { data, error } = await supabase
      .from('kiosk_submissions')
      .select('recording_id')
      .eq('id', submissionId)
      .maybeSingle();
    if (error || !data?.recording_id) return null;

    const { data: perf } = await supabase
      .from('performances')
      .select('id')
      .eq('kiosk_recording_id', data.recording_id)
      .maybeSingle();
    if (!perf) return null;

    const { data: analysis } = await supabase
      .from('performance_analysis')
      .select('updated_at')
      .eq('performance_id', perf.id)
      .maybeSingle();

    return analysis?.updated_at || null;
  },

  async adminDuplicateLeague(sourceId: string, overrides?: { name?: string; slug?: string }): Promise<Record<string, unknown>> {
    const source = await this.adminGetLeague(sourceId);
    if (!source) throw new Error('Source league not found');

    const newSlug = overrides?.slug || `copy-of-${source.slug}`;
    const newName = overrides?.name || `Copy of ${source.name}`;

    const payload: Record<string, unknown> = {
      name: newName,
      slug: newSlug,
      description: source.description || '',
      status: 'draft',
      primary_color: source.primary_color || '#0ea5e9',
      secondary_color: source.secondary_color || '#0284c7',
      accent_color: source.accent_color || '#f59e0b',
      logo_url: source.logo_url || null,
      hero_image_url: source.hero_image_url || null,
      background_image_url: source.background_image_url || null,
      signup_headline: source.signup_headline || '',
      signup_subtext: source.signup_subtext || '',
      sponsor_logos: source.sponsor_logos || [],
      sponsor_links: source.sponsor_links || [],
      scoring_mode: source.scoring_mode || 'best',
      leaderboard_top_n: source.leaderboard_top_n || null,
      signup_mode: source.signup_mode || 'email_verified',
      allow_playback_default: source.allow_playback_default ?? true,
      rules: source.rules || null,
      rules_markdown: source.rules_markdown || null,
    };

    const { data: newLeague, error } = await supabase
      .from('kiosk_leagues')
      .insert(payload)
      .select()
      .single();
    if (error) throw new Error(error.message);

    const songs = await this.adminListLeagueSongs(sourceId);
    for (const song of songs) {
      const songId = typeof song.songs === 'object' && song.songs ? (song.songs as any).id : song.song_id;
      await this.adminAddLeagueSong(newLeague.id, songId, song.display_order);
    }

    return newLeague;
  },

  async triggerMix(kioskRecordingId: string) {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    const res = await fetch(`${supabaseUrl}/functions/v1/kiosk-mix-audio`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify({ kiosk_recording_id: kioskRecordingId }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Failed to trigger mix');
    return json as { success: boolean; command_id: string; kiosk_recording_id: string };
  },
};
